import { Link, useParams } from "react-router-dom";
import { Flower2, ArrowRight } from "lucide-react";
import { t } from "../i18n";
import Seo from "./Seo";

const COPY = {
  en: { code: "Lost petal", title: "This page has wilted", sub: "The page you're looking for isn't blooming here anymore — but our team across Morocco still is.",
        home: "Back to home", cities: "Browse cities", occasions: "Shop by occasion", help: "Need a hand? Contact us" },
  fr: { code: "Pétale égaré", title: "Cette page a fané", sub: "La page que vous cherchez ne fleurit plus ici — mais notre équipe couvre tout le Maroc.",
        home: "Retour à l'accueil", cities: "Parcourir les villes", occasions: "Par occasion", help: "Besoin d'aide ? Contactez-nous" },
  es: { code: "Pétalo perdido", title: "Esta página se ha marchitado", sub: "La página que busca ya no florece aquí, pero nuestro equipo en Marruecos sí.",
        home: "Volver al inicio", cities: "Ver ciudades", occasions: "Por ocasión", help: "¿Necesita ayuda? Contáctenos" },
  ar: { code: "بتلة ضائعة", title: "هذه الصفحة قد ذبلت", sub: "الصفحة التي تبحث عنها لم تعد تُزهر هنا — لكن فريقنا في جميع أنحاء المغرب ما زالوا.",
        home: "العودة للرئيسية", cities: "تصفّح المدن", occasions: "حسب المناسبة", help: "تحتاج مساعدة؟ تواصل معنا" },
  it: { code: "Petalo perduto", title: "Questa pagina è appassita", sub: "La pagina che cerca non fiorisce più qui — ma il nostro team in tutto il Marocco è ancora attivo.",
        home: "Torna alla home", cities: "Esplora le città", occasions: "Acquista per occasione", help: "Ha bisogno di aiuto? Contattaci" },
  nl: { code: "Verdwaald bloemblaadje", title: "Deze pagina is verwelkt", sub: "De pagina die u zoekt bloeit hier niet meer — maar ons team door heel Marokko wel.",
        home: "Terug naar home", cities: "Bekijk steden", occasions: "Shop per gelegenheid", help: "Hulp nodig? Neem contact op" },
  de: { code: "Verlorenes Blütenblatt", title: "Diese Seite ist verwelkt", sub: "Die gesuchte Seite blüht hier nicht mehr auf — aber unser Team in ganz Marokko schon.",
        home: "Zurück zur Startseite", cities: "Städte entdecken", occasions: "Nach Anlass stöbern", help: "Brauchen Sie Hilfe? Kontaktieren Sie uns" },
};

export default function NotFound({ message }) {
  const { locale = "en" } = useParams();
  const c = COPY[locale] || COPY.en;
  return (
    <div className="relative min-h-[70vh] flex items-center overflow-hidden bg-plaster" data-testid="not-found" dir={locale === "ar" ? "rtl" : "ltr"}>
      <div className="absolute -top-24 -end-24 w-96 h-96 rounded-full bg-brass-light/20 blur-3xl" aria-hidden />
      <div className="absolute -bottom-32 -start-16 w-80 h-80 rounded-full bg-ink/5 blur-3xl" aria-hidden />
      <div className="relative max-w-content mx-auto px-6 py-20 w-full">
        <Seo title="404 — Fleurinelle" noindex />
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 text-brass mb-6">
            <Flower2 size={20} />
            <span className="smallcaps text-xs tracking-[0.24em]">{c.code} · 404</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">{c.title}</h1>
          <p className="text-charcoal mt-5 text-base leading-relaxed">{c.sub}</p>
          {message ? <p className="text-muted text-xs mt-3">{message}</p> : null}
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to={`/${locale}`} data-testid="nf-home" className="btn-primary inline-flex items-center gap-2">
              {c.home} <ArrowRight size={16} />
            </Link>
            <Link to={`/${locale}/cities`} data-testid="nf-cities" className="btn-secondary">{c.cities}</Link>
            <Link to={`/${locale}/occasions`} data-testid="nf-occasions" className="btn-secondary">{c.occasions}</Link>
          </div>
          <Link to={`/${locale}/contact`} className="inline-block mt-8 text-sm text-brass hover:text-brass-dark underline underline-offset-4" data-testid="nf-help">
            {c.help}
          </Link>
        </div>
      </div>
    </div>
  );
}
